import { useCallback, useMemo } from 'react'
import { collection, query, where, doc, serverTimestamp, deleteField } from 'firebase/firestore'
import type { Query } from 'firebase/firestore'
import { setDoc, deleteDoc, updateDoc } from '../lib/firestore'
import { db } from '../lib/firebase'
import { useFirestoreCollection } from './useFirestore'
import { useGroupsTotems } from './useGroupTotems'
import { useTotemFollows, totemRefKey } from './useTotemFollows'
import { runMutation } from '../lib/firestoreMutation'
import { track } from '../lib/analytics'
import { canEditTotem } from '../lib/totemPermissions'
import type { Group, Totem, TotemSlot } from '../types'
import { fbt } from 'fbtee'

export const totemSlotId = (groupId: string, totemId: string, setId: string) =>
  `${groupId}_${totemId}_${setId}`

export interface EligibleTotem {
  group: Group
  totem: Totem
}

export interface PlacedTotem {
  /** Absent for a followed totem — the group is one the viewer isn't in, so we
   *  never load its doc. Consumers must treat "no group" as "not mine". */
  group?: Group
  totem: Totem
  slot: TotemSlot
}

// Bundle threaded down to set cards (ScheduleView → DayGrid → SetBlock), same
// shape of arrangement as SetFeed. Undefined when the surface has nothing
// totem-related to show or do.
export interface TotemMarking {
  eligibleTotems: EligibleTotem[]
  slotsBySet: Map<string, PlacedTotem[]>
  addSlot: (groupId: string, totemId: string, setId: string) => void
  removeSlot: (groupId: string, totemId: string, setId: string) => void
  updateSlotPartialRange: (
    groupId: string,
    totemId: string,
    setId: string,
    partialRange: { startMin: number; endMin: number } | null,
  ) => void
  /** Opens the totem's route view. Supplied by festival mode only. */
  onOpenTotem?: (groupId: string, totemId: string) => void
  isFollowing: (groupId: string, totemId: string) => boolean
  unfollowTotem: (groupId: string, totemId: string) => void
}

export function useTotemSlots(eventId: string, userId: string | null, groups: Group[]) {
  const ref = userId
    ? (query(collection(db, 'totemSlots'), where('eventId', '==', eventId)) as Query<TotemSlot>)
    : null
  const { data, loading: slotsLoading, error } = useFirestoreCollection<TotemSlot>(ref)
  const { followedRefs, followedKeys, isFollowing, followTotem, unfollowTotem, loading: followsLoading } =
    useTotemFollows(userId)
  const { totemsByGroup, totemsById, loading: totemsLoading } = useGroupsTotems(groups, followedRefs)

  const groupsById = useMemo(() => new Map(groups.map(g => [g.id, g])), [groups])

  // Visible slots = totems from groups you're in, plus totems you follow. The
  // event query returns every group's slots, so this filter is what keeps a
  // stranger's totems off your schedule.
  const slots = useMemo(
    () =>
      data.filter(
        s => groupsById.has(s.groupId) || followedKeys.has(totemRefKey(s.groupId, s.totemId)),
      ),
    [data, groupsById, followedKeys],
  )

  const slotsBySet = useMemo(() => {
    const map = new Map<string, PlacedTotem[]>()
    for (const slot of slots) {
      const group = groupsById.get(slot.groupId)
      const totem = totemsById.get(slot.totemId)
      if (!totem) continue
      const placed: PlacedTotem = { group, totem, slot }
      const list = map.get(slot.setId)
      if (list) list.push(placed)
      else map.set(slot.setId, [placed])
    }
    return map
  }, [slots, groupsById, totemsById])

  const slotsByGroup = useMemo(() => {
    const map = new Map<string, TotemSlot[]>()
    for (const slot of slots) {
      const list = map.get(slot.groupId)
      if (list) list.push(slot)
      else map.set(slot.groupId, [slot])
    }
    return map
  }, [slots])

  const addSlot = useCallback(
    async (groupId: string, totemId: string, setId: string) => {
      if (!userId) return
      await runMutation(fbt("Couldn't mark the totem", 'Error toast when placing a totem at a set fails'), async () => {
        await setDoc(doc(db, 'totemSlots', totemSlotId(groupId, totemId, setId)), {
          groupId,
          totemId,
          eventId,
          setId,
          createdBy: userId,
          createdAt: serverTimestamp(),
        })
        track('totem_slot_added', { group_id: groupId, event_id: eventId })
      })
    },
    [userId, eventId],
  )

  const removeSlot = useCallback(
    async (groupId: string, totemId: string, setId: string) => {
      await runMutation(fbt("Couldn't remove the totem", 'Error toast when removing a totem from a set fails'), async () => {
        await deleteDoc(doc(db, 'totemSlots', totemSlotId(groupId, totemId, setId)))
        track('totem_slot_removed', { group_id: groupId, event_id: eventId })
      })
    },
    [eventId],
  )

  const updateSlotPartialRange = useCallback(
    async (
      groupId: string,
      totemId: string,
      setId: string,
      partialRange: { startMin: number; endMin: number } | null,
    ) => {
      await runMutation(fbt("Couldn't update the totem time", 'Error toast when changing the part of a set a totem will be at fails'), async () => {
        const ref = doc(db, 'totemSlots', totemSlotId(groupId, totemId, setId))
        if (partialRange) {
          await updateDoc(ref, { partialRange })
          track('totem_partial_range_set', {
            group_id: groupId, event_id: eventId,
            start_min: partialRange.startMin, end_min: partialRange.endMin,
          })
        } else {
          await updateDoc(ref, { partialRange: deleteField() })
          track('totem_partial_range_cleared', {
            group_id: groupId, event_id: eventId,
          })
        }
      })
    },
    [eventId],
  )

  return {
    slots,
    slotsBySet,
    slotsByGroup,
    totemsByGroup,
    totemsById,
    loading: slotsLoading || totemsLoading || followsLoading,
    error,
    addSlot,
    removeSlot,
    updateSlotPartialRange,
    isFollowing,
    followTotem,
    unfollowTotem,
  }
}

// Page-level convenience: the TotemMarking bundle for a schedule surface plus
// the per-group slot map for "where is the totem" sections. Eligible totems =
// totems from the user's groups linked to this event that the user may edit.
export function useTotemMarking(
  eventId: string,
  userId: string | null,
  groups: Group[],
  linkedGroups: Group[],
) {
  const {
    slotsBySet, slotsByGroup, totemsByGroup, loading,
    addSlot, removeSlot, updateSlotPartialRange, isFollowing, unfollowTotem,
  } = useTotemSlots(eventId, userId, groups)

  const marking = useMemo<TotemMarking | undefined>(() => {
    // Eligibility comes from the editor lists of totems in groups you're in,
    // never from `slotsBySet`. Following a totem shows you where it's going; it
    // never lets you move it.
    const eligibleTotems: EligibleTotem[] = []
    for (const group of linkedGroups) {
      const totems = totemsByGroup.get(group.id) ?? []
      for (const totem of totems) {
        if (canEditTotem(totem, group, userId)) eligibleTotems.push({ group, totem })
      }
    }
    if (eligibleTotems.length === 0 && slotsBySet.size === 0) return undefined
    return {
      eligibleTotems, slotsBySet, addSlot, removeSlot, updateSlotPartialRange,
      isFollowing, unfollowTotem,
    }
  }, [
    linkedGroups, userId, totemsByGroup, slotsBySet,
    addSlot, removeSlot, updateSlotPartialRange, isFollowing, unfollowTotem,
  ])

  return { marking, slotsByGroup, totemsByGroup, loading }
}
